import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Filter, Crown, Settings2, Search } from "lucide-react";
import * as XLSX from "xlsx";
import { api } from "../../lib/api";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, Badge, SectionTitle,
} from "../../components/common/Brutal";

export default function AdminVVIPTracker() {
  const [scans, setScans] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", staff_id: "" });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.staff_id) params.staff_id = filters.staff_id;
      const { data } = await api.get("/vvip-scans", { params });
      setScans(data);
    } catch (e) {
      toast.error("Failed to load scans");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/users").then(({ data }) => setStaff(data.filter((u) => u.role === "staff_vvip")));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!search) return scans;
    const q = search.toLowerCase();
    return scans.filter((s) =>
      (s.qr_code || "").toLowerCase().includes(q) ||
      (s.qr_label || "").toLowerCase().includes(q) ||
      (s.qr_area || "").toLowerCase().includes(q) ||
      (s.staff_name || "").toLowerCase().includes(q)
    );
  }, [scans, search]);

  const exportXLSX = () => {
    const rows = filtered.map((s) => {
      const d = new Date(s.scanned_at);
      return {
        Date: d.toLocaleDateString(),
        Time: d.toLocaleTimeString(),
        "QR Code": s.qr_code,
        Label: s.qr_label,
        Area: s.qr_area,
        Staff: s.staff_name,
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "VVIP Scans");
    XLSX.writeFile(wb, `vvip-scans-${new Date().toISOString().slice(0,10)}.xlsx`);
    toast.success("Exported");
  };

  return (
    <div className="space-y-6" data-testid="admin-vvip-tracker">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionTitle kicker="VVIP Operations" title="VVIP Scan Tracker" icon={Crown} />
        <Link to="/admin/vvip-codes" data-testid="manage-vvip-codes">
          <BrutalButton variant="primary"><Settings2 className="h-4 w-4" /> Manage VVIP QR Codes</BrutalButton>
        </Link>
      </div>

      <BrutalCard className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-[#E5247C]" />
          <div className="text-xs font-bold uppercase tracking-widest text-[#6B4E9E]">Filters</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <BrutalLabel>Date From</BrutalLabel>
            <BrutalInput type="date" value={filters.date_from} onChange={(e) => setFilters({...filters, date_from: e.target.value})} data-testid="vvip-date-from" />
          </div>
          <div>
            <BrutalLabel>Date To</BrutalLabel>
            <BrutalInput type="date" value={filters.date_to} onChange={(e) => setFilters({...filters, date_to: e.target.value})} data-testid="vvip-date-to" />
          </div>
          <div>
            <BrutalLabel>Staff (VVIP)</BrutalLabel>
            <select
              className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5"
              value={filters.staff_id}
              onChange={(e) => setFilters({...filters, staff_id: e.target.value})}
              data-testid="vvip-staff-filter"
            >
              <option value="">All staff_vvip</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || `${s.firstname} ${s.lastname}`.trim()}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <BrutalButton variant="primary" onClick={load} className="flex-1" data-testid="vvip-apply"><Filter className="h-4 w-4" /> Apply</BrutalButton>
            <BrutalButton variant="outline" onClick={() => { setFilters({ date_from: "", date_to: "", staff_id: "" }); }} data-testid="vvip-clear">Clear</BrutalButton>
          </div>
        </div>
      </BrutalCard>

      <BrutalCard className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-3">
            <Badge color="magenta" data-testid="vvip-total-records">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</Badge>
            <div className="text-xs uppercase tracking-widest text-[#6B4E9E]">total scans (filtered)</div>
          </div>
          <div className="flex items-center gap-2 flex-1 max-w-md min-w-[200px]">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B4E9E]" />
              <BrutalInput className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search QR / staff..." data-testid="vvip-search" />
            </div>
            <BrutalButton variant="outline" onClick={exportXLSX} data-testid="vvip-export"><Download className="h-4 w-4" /> Excel</BrutalButton>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vvip-scans-table">
            <thead className="bg-[#FFB400]">
              <tr>
                {["Date","Time","QR Code","Staff"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest border-b-2 border-[#3D1E6D]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => {
                const d = new Date(s.scanned_at);
                return (
                  <tr key={s.id} className={i % 2 ? "bg-[#FFF4E6]" : "bg-white"} data-testid={`vvip-scan-${s.id}`}>
                    <td className="px-3 py-2 font-bold">{d.toLocaleDateString()}</td>
                    <td className="px-3 py-2 text-[#6B4E9E]">{d.toLocaleTimeString()}</td>
                    <td className="px-3 py-2">
                      <div className="font-bold">{s.qr_code}</div>
                      {(s.qr_label || s.qr_area) && (
                        <div className="text-[10px] text-[#6B4E9E] uppercase tracking-wider">
                          {s.qr_label}{s.qr_label && s.qr_area ? " · " : ""}{s.qr_area}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">{s.staff_name}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-[#6B4E9E]">
                    {loading ? "Loading…" : "No VVIP scans match these filters."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </BrutalCard>
    </div>
  );
}
